import { useQueryClient } from "@tanstack/react-query";
import { setDefaultOptions } from "date-fns";
import {
  FC,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import Button from "@ingka/button";
import ListView, { ListViewItem } from "@ingka/list-view";
import SSRIcon from "@ingka/ssr-icon";
import ArrowClockwiseIcon from "@ingka/ssr-icon/paths/arrow-clockwise";
import ChevronDownIcon from "@ingka/ssr-icon/paths/chevron-down-small";
import ChevronRightIcon from "@ingka/ssr-icon/paths/chevron-right-small";
import {
  getWeekdayRegionOffs,
  naturalSortListByAttribute,
} from "@pdpp/lib-planta";
import { PopOver, useBoolean } from "@pdpp/lib-react";

import { css, cx } from "../__generated-styled-system/css";
import { weekStartsOn$ } from "../features/dates/state";
import { showAdminActions } from "../features/experimental/transforms";
import { refreshServerOrgCache } from "../features/org/api";
import { countryCode$, ORG_NODE_BY_ID, orgId$ } from "../features/org/state";
import {
  getAncestors,
  getChildrenWithoutActivities,
  getOrgNodeDisplayName,
  getSelectBreadCrumbs,
  getSiblings,
} from "../features/org/transforms";
import { ClientBusinessNode } from "../features/org/types";
import { Intralink } from "./Intralink";

const containerStyles = css({
  boxShadow: "shadowSizeDefault",
  backgroundColor: "colourStaticWhite",
  width: "640px",
  height: "500px",
  "@media (width < 700px)": {
    width: "380px",
    height: "240px",
  },
});

const headerStyles = css({
  padding: "space100!",
  display: "flex",
  alignItems: "center",
  gap: "space25",
  borderStyle: "solid!",
  borderBottomColor: "colourStaticGrey!",
  borderBottomWidth: "1px!",
  height: "57px",
});

const contentStyles = css({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  height: "calc(100% - 57px)",
});

const selectorWrapperStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "space50",
});

const activeItemStyles = css({
  backgroundColor: "colourStaticLightGrey",
});

const breadcrumbsWrapper = css({
  display: "flex",
  alignItems: "center",
});

const subListStyles = css({
  borderStyle: "solid!",
  borderLeftColor: "colourStaticGrey!",
  borderLeftWidth: "1px!",
});

const listViewStyles = css({
  overflowY: "auto",
});

const buttonStyles = css({
  alignSelf: "flex-start",
  margin: "-0.3rem 0 0 -1.4rem!",
  fontSize: "1rem!",
});

const intraLinkStyles = css({
  fontSize: "0.75rem!",
});

const adminActionsWrapperStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "space50",
});

const selectRowStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "space50",
});

const refreshButtonStyles = css({
  marginTop: "-0.3rem!",
  "& svg": {
    transition: "transform 0.4s ease",
    transform: "rotate(0deg)",
  },
  _hover: {
    "&:not(:disabled) svg": {
      transform: "rotate(180deg)",
    },
  },
});

const refreshSuccessStyles = css({
  display: "inline-flex",
  alignItems: "center",
  fontSize: "0.75rem!",
  color: "colourSemanticPositive",
  whiteSpace: "nowrap",
});

interface OrgItemProps {
  readonly node: ClientBusinessNode;
  readonly hasChildren: boolean;
  readonly analytics?: string;
}

const OrgItem: FC<OrgItemProps> = memo(function OrgItem$({
  node,
  hasChildren,
  analytics,
}) {
  const onClick =
    node.type === "activity"
      ? undefined
      : () => {
          orgId$.value = node.id;
        };

  const isActive = node.id === orgId$.value;
  const { i18n } = useTranslation();

  return (
    <ListViewItem
      id={node.id}
      className={isActive ? activeItemStyles : undefined}
      title={getOrgNodeDisplayName(node, i18n.language)}
      control="navigational"
      controlIcon={isActive && hasChildren ? "chevronRightSmall" : null}
      inset={true}
      emphasised={false}
      size="small"
      showDivider={false}
      onClick={onClick}
      data-analytics={analytics}
    />
  );
});

interface LinkItemProps {
  readonly node: ClientBusinessNode;
  readonly locale: string;
}

const LinkItem: FC<LinkItemProps> = memo(function LinkItem$({ node, locale }) {
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  params.set("orgNode", node.id);

  useEffect(() => {
    if (node.type !== "country") return;
    weekStartsOn$.value = getWeekdayRegionOffs(node.id);
    setDefaultOptions({ weekStartsOn: weekStartsOn$.value });
  }, [node]);

  return (
    <Fragment>
      <Intralink
        className={intraLinkStyles}
        text={getOrgNodeDisplayName(node, locale)}
        url={`${location.pathname}?${params.toString()}`}
        onClick={() => {
          orgId$.value = node.id;
        }}
        disabled={node.type === "country"}
      />
      <SSRIcon paths={ChevronRightIcon} />
    </Fragment>
  );
});

interface OrganisationSelectProps {
  readonly nodes: ReadonlyArray<ClientBusinessNode>;
  readonly orgId: string | undefined;
}
export const OrganisationSelect: FC<OrganisationSelectProps> = memo(
  function OrganisationSelect$({ nodes, orgId }) {
    const [t, i18next] = useTranslation();
    const queryClient = useQueryClient();
    const [isPopoverShown, , hidePopover, togglePopover] = useBoolean();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshSuccess, setRefreshSuccess] = useState(false);

    const handleRefresh = useCallback(async () => {
      setIsRefreshing(true);
      try {
        await refreshServerOrgCache(countryCode$.value);
        await queryClient.invalidateQueries({ queryKey: ["org-structure"] });
        setRefreshSuccess(true);
      } catch (error) {
        console.error("Failed to refresh organisation cache: %o", error);
      } finally {
        setIsRefreshing(false);
      }
    }, [queryClient]);

    const breadCrumbs = useMemo(
      () => getSelectBreadCrumbs(nodes, orgId),
      [nodes, orgId],
    );
    const selected = ORG_NODE_BY_ID.get(orgId ?? "");
    const ancestors = useMemo(
      () => [...getAncestors(nodes, orgId)],
      [nodes, orgId],
    );
    const siblings = useMemo(
      () => naturalSortListByAttribute([...getSiblings(nodes, orgId)], "name"),
      [nodes, orgId],
    );
    const childrenWithoutActivities = useMemo(
      () =>
        naturalSortListByAttribute(
          [...getChildrenWithoutActivities(nodes, orgId)],
          "name",
        ),
      [nodes, orgId],
    );
    const parentIdsWithChildren = useMemo(() => {
      const ids = new Set<string>();
      for (const node of nodes) {
        if (node.parentId !== undefined && node.type !== "activity") {
          ids.add(node.parentId);
        }
      }
      return ids;
    }, [nodes]);

    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    return (
      <div>
        <div className={selectorWrapperStyles}>
          <div className={breadcrumbsWrapper}>
            {breadCrumbs.map((n) => (
              <LinkItem key={n.id} node={n} locale={i18next.language} />
            ))}
          </div>
          <div className={selectRowStyles}>
            <Button
              ref={setAnchorEl}
              className={buttonStyles}
              text={
                selected
                  ? getOrgNodeDisplayName(selected, i18next.language)
                  : ""
              }
              type="tertiary"
              size="small"
              icon={{
                paths: ChevronDownIcon,
                position: "trailing",
              }}
              onClick={togglePopover}
              data-analytics="plan/organisation-select"
            />
            {showAdminActions() && (
              <div className={adminActionsWrapperStyles}>
                {!refreshSuccess && (
                  <Button
                    type="tertiary"
                    size="small"
                    iconOnly
                    ssrIcon={ArrowClockwiseIcon}
                    className={refreshButtonStyles}
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    loading={isRefreshing}
                    text={
                      isRefreshing
                        ? t("labels.loading")
                        : t("labels.refreshOrgCache")
                    }
                    data-analytics="plan/organisation-select/refresh-cache"
                  />
                )}
                {refreshSuccess && (
                  <span className={refreshSuccessStyles}>
                    {t("labels.refreshSuccess")}
                  </span>
                )}
              </div>
            )}
          </div>
          {isPopoverShown && (
            <PopOver
              clickAwayHandler={hidePopover}
              anchorEl={anchorEl}
              horizontalAlignment="left"
              horizontalPosition="left"
              verticalPosition="bottom"
              verticalAlignment="top"
              onScroll={hidePopover}
            >
              <div className={containerStyles}>
                <div className={headerStyles}>
                  {ancestors.map((n) => (
                    <LinkItem key={n.id} node={n} locale={i18next.language} />
                  ))}
                </div>
                <div className={contentStyles}>
                  <ListView
                    className={listViewStyles}
                    id="org-select-1"
                    size="small"
                  >
                    {siblings.map((n) => (
                      <OrgItem
                        key={n.id}
                        node={n}
                        hasChildren={parentIdsWithChildren.has(n.id)}
                        analytics={`plan/organisation-select/${n.name}`}
                      />
                    ))}
                  </ListView>
                  <ListView
                    className={cx(subListStyles, listViewStyles)}
                    id="org-select-2"
                    size="small"
                  >
                    {selected !== undefined &&
                      childrenWithoutActivities.map((n) => (
                        <OrgItem
                          key={n.id}
                          node={n}
                          hasChildren={parentIdsWithChildren.has(n.id)}
                          analytics={`plan/organisation-select/${n.name}`}
                        />
                      ))}
                  </ListView>
                </div>
              </div>
            </PopOver>
          )}
        </div>
      </div>
    );
  },
);
